import { useState, useMemo, useEffect, useCallback } from 'react';
import { DataGrid, KeenIcon, Modal } from '@/components';
import { ConfirmationDialog } from '@/components/dialogBox';
import usePagination from '@/hooks/usePagination';
import {
  useGetServiceBookingsQuery,
  useApproveServiceBookingMutation,
  useRejectServiceBookingMutation,
  useUpdateServiceProviderBookingStatusMutation,
  useGetServiceBookingNoShowSummaryMutation,
  useMarkServiceBookingNoShowMutation,
} from '@/services/ServiceProviderBookingService';
import {
  BookingStatus,
  Booking,
  BookingNoShowSummary,
} from '@/types/booking-management.types';
import { useNavigate } from 'react-router-dom';
import { usePermissions } from '@/hooks/usePermissions';
import { formatFinnishCurrency } from '@/utils';
import { RefundMode } from '@/constant';
import { formatDisplayDate } from '@/utils/Date';
import { enqueueSnackbar } from 'notistack';
import { ErrorApiResponse } from '@/types/api.types';

export interface ServiceBookingManagementProps {
  harborId?: string;
  serviceProviderId?: string;
  handleViewClick?: (bookingId: string) => void;
}

interface ServiceBookingRow {
  id: string;
  user: {
    id: string;
    name: string | null;
    email: string;
  };
  berth: {
    id: string;
    berth_number: string;
  };
  service_name: string;
  start_date: string;
  start_time: string | null;
  end_date: string;
  total_price: number;
  payment_type: number;
  status: number;
  special_requests: string | null;
  cancellation_reason: string | null;
  created_at: string;
}

const ServiceBookingManagement = (props: ServiceBookingManagementProps) => {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const {
    totalPages,
    totalItems,
    handleTotalPages,
    handleTotalItems,
    debouncedValue,
    handleSearch,
    searchTerm,
    pagination,
    setPagination,
    resetSearch,
  } = usePagination();

  const [confirmActionState, setConfirmActionState] = useState<{
    isOpen: boolean;
    bookingId: string | null;
    action: 'approve' | 'reject' | 'cancel' | null;
    reason?: string;
  }>({ isOpen: false, bookingId: null, action: null });

  const [noShowState, setNoShowState] = useState<{
    isOpen: boolean;
    bookingId: string | null;
    mode: RefundMode | null;
    summary: BookingNoShowSummary | null;
    note: string;
    step: 'choice' | 'summary';
  }>({
    isOpen: false,
    bookingId: null,
    mode: null,
    summary: null,
    note: '',
    step: 'choice',
  });

  const [statusFilter, setStatusFilter] = useState<string>('all');

  const getStatusEnum = (filter: string): number | undefined => {
    const statusMap: { [key: string]: number } = {
      pending: BookingStatus.PENDING, // 1
      confirmed: BookingStatus.CONFIRMED, // 2
      cancelled: BookingStatus.CANCELLED, // 3
      completed: BookingStatus.COMPLETED, // 4
      noshow: BookingStatus.PENDING_CONFIRMATION, // 5
      rejected: BookingStatus.REJECTED, // 6
    };
    return filter !== 'all' ? statusMap[filter] : undefined;
  };

  const queryParams = {
    page: pagination.pageIndex + 1,
    limit: pagination.pageSize,
    search: debouncedValue,
    status: getStatusEnum(statusFilter),
    harbor_id: props.harborId,
    service_provider_id: props.serviceProviderId,
  };

  const { data, isLoading, isFetching, isError, refetch } =
    useGetServiceBookingsQuery(queryParams);

  const [approveBooking, { isLoading: isApproving }] =
    useApproveServiceBookingMutation();
  const [rejectBooking, { isLoading: isRejecting }] =
    useRejectServiceBookingMutation();
  const [updateStatus, { isLoading: isUpdatingStatus }] =
    useUpdateServiceProviderBookingStatusMutation();
  const [
    getServiceBookingNoShowSummary,
    { isLoading: isLoadingNoShowSummary },
  ] = useGetServiceBookingNoShowSummaryMutation();
  const [markServiceBookingNoShow, { isLoading: isMarkingNoShow }] =
    useMarkServiceBookingNoShowMutation();

  const isNoShowProcessing = isLoadingNoShowSummary || isMarkingNoShow;
  const isUpdating = isApproving || isRejecting || isUpdatingStatus;

  useEffect(() => {
    if (data) {
      handleTotalPages(data.totalPages || 1);
      handleTotalItems(data.total || 0);
    }
  }, [data, handleTotalPages, handleTotalItems]);

  useEffect(() => {
    if (typeof refetch === 'function') {
      refetch();
    }
  }, [
    statusFilter,
    debouncedValue,
    props.harborId,
    props.serviceProviderId,
    pagination,
    refetch,
  ]);

  const filteredData = useMemo((): ServiceBookingRow[] => {
    if (!data?.data) return [];

    return data.data.map(
      (booking: Booking): ServiceBookingRow => ({
        id: booking.id,
        user: {
          id: booking.user.id,
          name: booking.user.name,
          email: booking.user.email,
        },
        berth: {
          id: booking.service?.id || '',
          berth_number: booking.service?.name || '-',
        },
        service_name: booking.service?.name || '-',
        start_date: booking.start_date,
        start_time: booking.start_time ?? null,
        end_date: booking.end_date,
        total_price: booking.total_price,
        payment_type: booking.payment_type,
        status: booking.status,
        special_requests: booking.special_requests || null,
        cancellation_reason: booking.cancellation_reason || null,
        created_at: booking.created_at,
      })
    );
  }, [data]);

  const { handleViewClick: propHandleViewClick } = props;

  const handleActionClick = useCallback(
    (bookingId: string, action: 'approve' | 'reject' | 'cancel') => {
      setConfirmActionState({ isOpen: true, bookingId, action });
    },
    [setConfirmActionState]
  );

  const handleViewClick = useCallback(
    (bookingId: string) => {
      if (propHandleViewClick) {
        propHandleViewClick(bookingId);
      } else {
        navigate(`/service-bookings/${bookingId}`);
      }
    },
    [propHandleViewClick, navigate]
  );

  const handleEditClick = useCallback(
    (bookingId: string) => {
      navigate(`/service-bookings/${bookingId}/edit`);
    },
    [navigate]
  );

  const handleNoShowClick = useCallback(
    (bookingId: string) => {
      setNoShowState({
        isOpen: true,
        bookingId,
        mode: null,
        summary: null,
        note: '',
        step: 'choice',
      });
    },
    [setNoShowState]
  );

  const handleCloseNoShowModal = () => {
    setNoShowState({
      isOpen: false,
      bookingId: null,
      mode: null,
      summary: null,
      note: '',
      step: 'choice',
    });
  };

  const handleNoShowNoteChange = (
    e: React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    const { value } = e.target;
    setNoShowState((prev) => ({
      ...prev,
      note: value,
    }));
  };

  const handleNoShowRefundOption = async () => {
    if (!noShowState.bookingId) return;
    try {
      const response = await getServiceBookingNoShowSummary(
        noShowState.bookingId
      ).unwrap();
      setNoShowState((prev) => ({
        ...prev,
        mode: RefundMode.REFUND_AS_POLICY,
        summary: response.data,
        step: 'summary',
      }));
    } catch (error) {
      const apiError = error as ErrorApiResponse;
      enqueueSnackbar(apiError?.data?.message || 'Failed to load no-show summary', { variant: 'error' });
    }
  };

  const handleNoShowNoRefundOption = async () => {
    if (!noShowState.bookingId) return;
    try {
      await markServiceBookingNoShow({
        id: noShowState.bookingId,
        data: {
          mode: RefundMode.NO_REFUND,
          note: noShowState.note.trim(),
        },
      }).unwrap();
      if (typeof refetch === 'function') {
        refetch();
      }
      handleCloseNoShowModal();
    } catch (error) {
      const apiError = error as ErrorApiResponse;
      enqueueSnackbar(apiError?.data?.message || 'Failed to mark booking as no-show', { variant: 'error' });
    }
  };

  const handleConfirmRefundNoShow = async () => {
    if (!noShowState.bookingId) return;
    try {
      await markServiceBookingNoShow({
        id: noShowState.bookingId,
        data: {
          mode: RefundMode.REFUND_AS_POLICY,
          note: noShowState.note.trim(),
        },
      }).unwrap();
      if (typeof refetch === 'function') {
        refetch();
      }
      handleCloseNoShowModal();
    } catch (error) {
      const apiError = error as ErrorApiResponse;
      enqueueSnackbar(apiError?.data?.message || 'Failed to mark booking as no-show', { variant: 'error' });
    }
  };

  const hasBookingStarted = useCallback((startDate: string, startTime?: string | null) => {
    if (!startDate) return false;
    const datePart = startDate.includes('T') ? startDate.split('T')[0] : startDate;
    const isoStr = startTime
      ? `${datePart}T${startTime}:00Z`
      : `${datePart}T00:00:00Z`;
    const start = new Date(isoStr);
    if (Number.isNaN(start.getTime())) return false;
    return start <= new Date();
  }, []);

  const handleConfirmAction = async () => {
    if (confirmActionState.bookingId && confirmActionState.action) {
      try {
        if (confirmActionState.action === 'approve') {
          await approveBooking(confirmActionState.bookingId).unwrap();
        } else if (confirmActionState.action === 'reject') {
          await rejectBooking(confirmActionState.bookingId).unwrap();
        } else if (confirmActionState.action === 'cancel') {
          await updateStatus({
            id: confirmActionState.bookingId,
            update: {
              status: BookingStatus.CANCELLED,
              cancellation_reason: confirmActionState.reason,
            },
          }).unwrap();
        }

        if (typeof refetch === 'function') {
          refetch();
        }
        setConfirmActionState({
          isOpen: false,
          bookingId: null,
          action: null,
          reason: undefined,
        });
      } catch (error) {
        console.error('Failed to update booking status:', error);
      }
    }
  };

  const resetFilters = () => {
    resetSearch();
    setStatusFilter('all');
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  };

  const columns = useMemo(
    () => [
      {
        accessor: (row: ServiceBookingRow) => row.user.name,
        id: 'user_name',
        header: 'User',
        enableSorting: false,
        cell: ({ row }: { row: { original: ServiceBookingRow } }) => (
          <span className="text-sm font-medium text-gray-900">
            {row.original.user.name || row.original.user.email}
          </span>
        ),
        meta: { className: 'min-w-[200px]' },
      },
      {
        accessor: (row: ServiceBookingRow) => row.service_name,
        id: 'service_name',
        header: 'Service',
        enableSorting: false,
        cell: ({ row }: { row: { original: ServiceBookingRow } }) => (
          <span className="text-gray-800 font-normal">
            {row.original.service_name || '-'}
          </span>
        ),
        meta: { className: 'min-w-[150px]' },
      },
      {
        accessor: (row: ServiceBookingRow) => row.start_date,
        id: 'start_date',
        header: 'Start Date',
        enableSorting: false,
        cell: ({ row }: { row: { original: ServiceBookingRow } }) => (
          <span className="text-gray-800 font-normal">
            {formatDisplayDate(new Date(row.original.start_date.split('T')[0]))}
          </span>
        ),
        meta: { className: 'min-w-[150px]' },
      },
      {
        accessor: (row: ServiceBookingRow) => row.end_date,
        id: 'end_date',
        header: 'End Date',
        enableSorting: false,
        cell: ({ row }: { row: { original: ServiceBookingRow } }) => (
          <span className="text-gray-800 font-normal">
            {formatDisplayDate(new Date(row.original.end_date.split('T')[0]))}
          </span>
        ),
        meta: { className: 'min-w-[150px]' },
      },
      {
        accessor: (row: ServiceBookingRow) => row.total_price,
        id: 'total_price',
        header: 'Total Price',
        enableSorting: false,
        cell: ({ row }: { row: { original: ServiceBookingRow } }) => (
          <span className="text-gray-800 font-normal">
            {row.original.total_price !== null &&
            row.original.total_price !== undefined
              ? formatFinnishCurrency(row.original.total_price)
              : '-'}
          </span>
        ),
        meta: { className: 'min-w-[100px]' },
      },
      {
        accessor: (row: ServiceBookingRow) => row.payment_type,
        id: 'payment_type',
        header: 'Payment Type',
        enableSorting: false,
        cell: ({ row }: { row: { original: ServiceBookingRow } }) => (
          <span className="text-gray-800 font-normal">
            {row.original.payment_type === 1
              ? 'Credit Card'
              : row.original.payment_type === 2
                ? 'Bank Transfer'
                : 'Other'}
          </span>
        ),
        meta: { className: 'min-w-[150px]' },
      },
      {
        accessor: (row: ServiceBookingRow) => row.status,
        id: 'status',
        header: 'Status',
        enableSorting: false,
        cell: ({ row }: { row: { original: ServiceBookingRow } }) => (
          <span
            data-testid={`status-${row.original.id}`}
            className={`text-sm font-medium ${
              row.original.status === BookingStatus.CONFIRMED
                ? 'text-green-600'
                : row.original.status === BookingStatus.CANCELLED
                  ? 'text-red-600'
                  : row.original.status === BookingStatus.COMPLETED
                    ? 'text-blue-600 contrast:text-blue-200'
                    : row.original.status === BookingStatus.PENDING_CONFIRMATION
                      ? 'text-gray-600'
                      : row.original.status === BookingStatus.REJECTED
                        ? 'text-red-600'
                        : 'text-yellow-600'
            }`}
          >
            {row.original.status === BookingStatus.PENDING
              ? 'Payment Pending'
              : row.original.status === BookingStatus.CONFIRMED
                ? 'Confirmed'
                : row.original.status === BookingStatus.CANCELLED
                  ? 'Cancelled'
                  : row.original.status === BookingStatus.COMPLETED
                    ? 'Completed'
                    : row.original.status === BookingStatus.PENDING_CONFIRMATION
                      ? 'Pending Confirmation'
                      : row.original.status === BookingStatus.REJECTED
                        ? 'Rejected'
                        : 'Payment Pending'}
          </span>
        ),
        meta: { className: 'min-w-[100px]' },
      },
      {
        accessor: (row: ServiceBookingRow) => row.special_requests,
        id: 'special_requests',
        header: 'Special Requests',
        enableSorting: false,
        cell: ({ row }: { row: { original: ServiceBookingRow } }) => (
          <span className="text-gray-800 font-normal">
            {row.original.special_requests || '-'}
          </span>
        ),
        meta: { className: 'min-w-[250px]' },
      },
      {
        accessor: (row: ServiceBookingRow) => row.created_at,
        id: 'created_at',
        header: 'Created At',
        enableSorting: false,
        cell: ({ row }: { row: { original: ServiceBookingRow } }) => (
          <span className="text-gray-800 font-normal">
            {formatDisplayDate(new Date(row.original.created_at))}
          </span>
        ),
        meta: { className: 'min-w-[150px]' },
      },
      {
        id: 'actions',
        header: 'Actions',
        enableSorting: false,
        cell: ({ row }: { row: { original: ServiceBookingRow } }) => (
          <div className="flex items-center gap-2">
            {hasPermission('service_management_read') && (
              <button
                type="button"
                onClick={() => handleViewClick(row.original.id)}
                className="btn btn-sm btn-icon btn-light btn-outline focus:outline-none focus:ring-2 focus:ring-blue-500"
                title="View"
                aria-label={`View ${row.original.user.name}'s booking`}
                disabled={isUpdating}
              >
                <KeenIcon type="filled" icon="eye" className="text-blue-500" />
              </button>
            )}
            {(row.original.status === BookingStatus.PENDING ||
              row.original.status === BookingStatus.PENDING_CONFIRMATION) &&
              hasPermission('service_management_update') && (
                <button
                  type="button"
                  onClick={() => handleEditClick(row.original.id)}
                  className="btn btn-sm btn-icon btn-light btn-outline focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  title="Edit"
                  aria-label={`Edit ${row.original.user.name}'s booking`}
                  disabled={isUpdating}
                >
                  <KeenIcon
                    type="filled"
                    icon="pencil"
                    className="text-yellow-500"
                  />
                </button>
              )}

            {row.original.status === BookingStatus.PENDING_CONFIRMATION &&
              hasPermission('service_management_update') && (
                <>
                  <button
                    type="button"
                    onClick={() =>
                      handleActionClick(row.original.id, 'approve')
                    }
                    className="btn btn-sm btn-icon btn-light btn-outline focus:outline-none focus:ring-2 focus:ring-green-500"
                    title="Approve"
                    aria-label={`Approve ${row.original.user.name}'s booking`}
                    disabled={isUpdating}
                  >
                    <KeenIcon
                      type="filled"
                      icon="check"
                      className="text-green-500"
                    />
                  </button>
                  <button
                    type="button"
                    onClick={() => handleActionClick(row.original.id, 'reject')}
                    className="btn btn-sm btn-icon btn-light btn-outline focus:outline-none focus:ring-2 focus:ring-red-500"
                    title="Reject"
                    aria-label={`Reject ${row.original.user.name}'s booking`}
                    disabled={isUpdating}
                  >
                    <KeenIcon
                      type="filled"
                      icon="cross"
                      className="text-red-500"
                    />
                  </button>
                </>
              )}
            {row.original.status === BookingStatus.CONFIRMED &&
              hasPermission('service_management_update') &&
              hasBookingStarted(row.original.start_date, row.original.start_time) && (
                <button
                  type="button"
                  onClick={() => handleNoShowClick(row.original.id)}
                  className="btn btn-sm btn-icon btn-light btn-outline focus:outline-none focus:ring-2 focus:ring-red-500"
                  title="Mark as No Show"
                  aria-label={`Mark ${row.original.user.name}'s booking as no show`}
                  disabled={isUpdating || isNoShowProcessing}
                >
                  <KeenIcon
                    type="filled"
                    icon="shield-slash"
                    className="text-red-500"
                  />
                </button>
              )}
          </div>
        ),
        meta: { className: 'w-[200px]' },
      },
    ],
    [
      handleViewClick,
      handleEditClick,
      handleActionClick,
      handleNoShowClick,
      hasPermission,
      hasBookingStarted,
      isUpdating,
      isNoShowProcessing,
    ]
  );

  return (
    <div className="berth-listings card card-grid w-full">
      <h1 className="sr-only">Service booking management</h1>
      <div className="card-header berth-header flex flex-wrap items-center gap-2 sm:gap-4 lg:gap-6 justify-between">
        <div className="berth-header-left flex items-center gap-2">
          <div className="flex-shrink-0">
            <label
              className="input input-sm h-8 sm:h-10 flex items-center"
              htmlFor="search-bookings"
              aria-label="Search bookings by user, service, or date"
            >
              <KeenIcon
                type="filled"
                icon="magnifier"
                className="text-gray-500"
              />
              <input
                id="search-bookings"
                placeholder="Search bookings"
                type="text"
                value={searchTerm}
                onChange={handleSearch}
                className="w-full sm:w-48 h-8 sm:h-10 text-sm input border-0 scale-y-[0.9] rounded-none"
                aria-label="Search bookings by user, service, or date"
              />
            </label>
          </div>
          <div className="flex-shrink-0">
            <select
              id="status-filter"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPagination((prev) => ({ ...prev, pageIndex: 0 }));
              }}
              className="select select-sm h-8 sm:h-10 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 pr-[30px]"
              aria-label="Filter bookings by status"
              disabled={isLoading || isFetching || isUpdating}
            >
              <option value="all">All</option>
              <option value="pending">Pending</option>
              <option value="confirmed">Confirmed</option>
              <option value="cancelled">Cancelled</option>
              <option value="completed">Completed</option>
              <option value="noshow">Pending Confirmation</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>
        </div>
        <div className="berth-header-right flex items-center gap-2">
          {searchTerm && searchTerm.trim() !== '' && (
            <button
              className="btn btn-sm btn-light flex items-center gap-2 h-8 sm:h-10 focus:outline-none focus:ring-2 focus:ring-blue-500"
              onClick={resetFilters}
              aria-label="Clear all filters"
              title="Clear all filters"
            >
              <KeenIcon
                type="solid"
                icon="arrows-circle"
                className="text-gray-600"
              />
              Clear Filters
            </button>
          )}
        </div>
      </div>
      <div className="card-body">
        {isLoading || isFetching || isUpdating ? (
          <div className="text-center py-4" role="status" aria-live="polite">
            <KeenIcon
              type="filled"
              icon="spinner"
              className="text-gray-500 text-2xl animate-spin"
            />
            <span className="ml-2 text-gray-700">Loading...</span>
          </div>
        ) : isError ? (
          <div className="text-center py-4 text-red-600" role="alert">
            Error loading data. Please try again.
          </div>
        ) : (
          <>
            {!isLoading && !isError && filteredData.length === 0 && (
              <div className="text-center py-4">
                <p className="text-gray-500">No Data available</p>
              </div>
            )}
            {!isLoading && !isError && filteredData.length > 0 && (
              <div className="berth-grid-wrapper">
                <DataGrid
                  data-testid="bookings-data-grid"
                  columns={columns}
                  data={filteredData}
                  rowSelect={false}
                  loading={isLoading || isFetching || isUpdating}
                  pagination={{
                    page: pagination.pageIndex,
                    size: pagination.pageSize,
                    totalItems: data?.total || totalItems,
                    totalPages: data?.totalPages || totalPages,
                    setPagination,
                    info: `{from} - {to} of {count}`,
                    sizes: [5, 10, 25, 50, 100],
                    sizesLabel: 'Show',
                    sizesDescription: 'per page',
                  }}
                  sorting={[{ id: 'user_name', desc: false }]}
                  serverSide={true}
                  hasActiveFilters={!!searchTerm || statusFilter !== 'all'}
                  aria-label="Bookings table"
                />
              </div>
            )}
          </>
        )}
        <ConfirmationDialog
          open={confirmActionState.isOpen}
          onClose={() =>
            setConfirmActionState({
              isOpen: false,
              bookingId: null,
              action: null,
              reason: undefined,
            })
          }
          onConfirm={handleConfirmAction}
          message={
            confirmActionState.action === 'cancel' ||
            confirmActionState.action === 'reject' ? (
              <>
                Are you sure you want to {confirmActionState.action} this
                booking?
                <input
                  type="text"
                  name="booking_action_reason"
                  aria-label="Action reason"
                  placeholder="Reason (optional)"
                  value={confirmActionState.reason || ''}
                  onChange={(e) =>
                    setConfirmActionState((prev) => ({
                      ...prev,
                      reason: e.target.value,
                    }))
                  }
                  className="mt-2 w-full p-2 border rounded bg-gray-200 placeholder:text-gray-400"
                  disabled={isUpdating}
                  autoComplete="off"
                />
              </>
            ) : (
              `Are you sure you want to approve this Booking?`
            )
          }
          cancelButtonText="No"
          confirmButtonText="Yes"
          aria-labelledby="action-confirmation-title"
          aria-describedby="action-confirmation-description"
        />
        <Modal
          open={noShowState.isOpen}
          onClose={handleCloseNoShowModal}
          className="!flex items-center justify-center"
          aria-label="No Show Booking"
        >
          <div className="bg-gray-100 rounded-lg w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto p-4 sm:p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Mark booking as no show
            </h2>
            {noShowState.step === 'choice' && (
              <>
                <p className="text-sm text-gray-700 mb-4">
                  Choose how you want to handle this no show. You can either
                  follow the refund policy or mark it as no refund.
                </p>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Note (optional)
                </label>
                <textarea
                  className="w-full border rounded-md p-2 text-sm text-gray-900 mb-4 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows={3}
                  value={noShowState.note}
                  onChange={handleNoShowNoteChange}
                  disabled={isNoShowProcessing}
                  placeholder="Add an internal note about this no show"
                />
                <div className="flex flex-col sm:flex-row gap-3 justify-end">
                  <button
                    type="button"
                    onClick={handleNoShowNoRefundOption}
                    className="btn btn-sm btn-light btn-outline text-red-600 border-red-200 hover:border-red-400 focus:outline-none focus:ring-2 focus:ring-red-500"
                    disabled={isNoShowProcessing}
                  >
                    Mark as No Show (No Refund)
                  </button>
                  <button
                    type="button"
                    onClick={handleNoShowRefundOption}
                    className="btn btn-sm btn-primary focus:outline-none focus:ring-2 focus:ring-blue-500"
                    disabled={isNoShowProcessing}
                  >
                    View Refund Summary
                  </button>
                </div>
              </>
            )}
            {noShowState.step === 'summary' && noShowState.summary && (
              <>
                <p className="text-sm text-gray-700 mb-4">
                  Review the refund summary before confirming this no show.
                </p>
                <div className="border rounded-md p-4 mb-4 bg-gray-50 text-sm text-gray-800">
                  <div className="flex justify-between mb-1">
                    <span>Booking total:</span>
                    <span>
                      {formatFinnishCurrency(
                        noShowState.summary.booking_total_price,
                        { currency: noShowState.summary.currency }
                      )}
                    </span>
                  </div>
                  <div className="flex justify-between mb-1">
                    <span>Net paid:</span>
                    <span>
                      {formatFinnishCurrency(noShowState.summary.net_paid, {
                        currency: noShowState.summary.currency,
                      })}
                    </span>
                  </div>
                  <div className="flex justify-between mb-1">
                    <span>Penalty base:</span>
                    <span>
                      {formatFinnishCurrency(noShowState.summary.penalty_base, {
                        currency: noShowState.summary.currency,
                      })}
                    </span>
                  </div>
                  <div className="flex justify-between mb-1">
                    <span>Penalty amount:</span>
                    <span>
                      {formatFinnishCurrency(
                        noShowState.summary.penalty_amount,
                        { currency: noShowState.summary.currency }
                      )}
                    </span>
                  </div>
                  <div className="flex justify-between font-semibold mt-2">
                    <span>Total refund:</span>
                    <span>
                      {formatFinnishCurrency(noShowState.summary.total_refund, {
                        currency: noShowState.summary.currency,
                      })}
                    </span>
                  </div>
                </div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Note (optional)
                </label>
                <textarea
                  className="w-full border rounded-md p-2 text-sm text-gray-900 mb-4 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows={3}
                  value={noShowState.note}
                  onChange={handleNoShowNoteChange}
                  disabled={isNoShowProcessing}
                  placeholder="Add an internal note about this no show"
                />
                <div className="flex flex-col sm:flex-row gap-3 justify-end">
                  <button
                    type="button"
                    onClick={() =>
                      setNoShowState((prev) => ({
                        ...prev,
                        step: 'choice',
                      }))
                    }
                    className="btn btn-sm btn-light focus:outline-none focus:ring-2 focus:ring-gray-400"
                    disabled={isNoShowProcessing}
                  >
                    Back
                  </button>
                  <button
                    type="button"
                    onClick={handleConfirmRefundNoShow}
                    className="btn btn-sm btn-primary focus:outline-none focus:ring-2 focus:ring-blue-500"
                    disabled={isNoShowProcessing}
                  >
                    Confirm No Show
                  </button>
                </div>
              </>
            )}
          </div>
        </Modal>
      </div>
    </div>
  );
};

export default ServiceBookingManagement;
